import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse, PaginationInfo, PaginatedResponse } from "./baseApi";
import { WorkOrder } from "./workOrderApi";
import { WorkOrderOperation } from "./workOrderOperationApi";
import { RawItem } from "./rawItemApi";
import { BomDetail } from "./bomDetailApi";
import { Endgood } from "./endgoodApi";
import { ItemCategory } from "./itemCategoryApi";

// Enums for MaterialIssue
export enum MaterialIssueType {
  PRODUCTION = 'PRODUCTION',
  MAINTENANCE = 'MAINTENANCE',
  REPAIR = 'REPAIR',
  SAMPLE = 'SAMPLE',
  TESTING = 'TESTING',
  SCRAP = 'SCRAP',
  OTHER = 'OTHER'
}

export enum MaterialIssueStatus {
  ISSUED = 'ISSUED',
  RETURNED = 'RETURNED',
  CONSUMED = 'CONSUMED',
  WASTED = 'WASTED',
  PARTIALLY_CONSUMED = 'PARTIALLY_CONSUMED',
  CANCELLED = 'CANCELLED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED'
}

// Main MaterialIssue interface
export interface MaterialIssue {
  id: number;
  issueDate: string;
  issueQuantity: number; // Changed to match Java entity
  consumedQuantity: number; // Changed to match Java entity
  returnedQuantity: number; // Changed to match Java entity
  wastedQuantity: number; // Changed to match Java entity
  unitCost?: number;
  totalCost?: number;
  issueType: MaterialIssueType;
  status: MaterialIssueStatus;
  issueReason?: string;
  wastageReason?: string;
  issuedBy?: string;
  receivedBy?: string;
  storeLocation?: string;
  rackNumber?: string;
  batchNumber?: string;
  lotNumber?: string; // Added to match Java entity
  heatNumber?: string;
  expiryDate?: string;
  qualityRemarks?: string;
  notes?: string; // Added to match Java entity
  isActive: boolean;
  branchId?: number;
  createdBy?: number;
  updatedBy?: number;
  createdAt: string;
  updatedAt: string;
  
  // Relationships
  workOrder: WorkOrder;
  workOrderOperation?: WorkOrderOperation;
  rawItem?: RawItem; // Optional - not present for internal manufactured parts
  endgood?: Endgood; // Optional - only present for internal manufactured parts
  rawCategory?: ItemCategory; // Category of the material
  rawItemVariantId?: number;
}

export interface MaterialIssueResponse extends PaginatedResponse<MaterialIssue> {}

// DTOs for API operations
export interface MaterialIssueCreateRequest {
  issueDate: string;
  issueQuantity: number; // Changed from quantityIssued to match Java entity
  consumedQuantity?: number; // Changed from quantityConsumed to match Java entity
  returnedQuantity?: number; // Added to match Java entity
  wastedQuantity?: number; // Added to match Java entity
  unitCost?: number;
  totalCost?: number;
  issueType: MaterialIssueType;
  status?: MaterialIssueStatus;
  issueReason?: string;
  wastageReason?: string; // Added to match Java entity
  issuedBy?: string;
  receivedBy?: string;
  storeLocation?: string;
  rackNumber?: string;
  batchNumber?: string;
  lotNumber?: string; // Added to match Java entity
  heatNumber?: string;
  expiryDate?: string;
  qualityRemarks?: string;
  notes?: string; // Added to match Java entity
  branchId?: number;
  workOrderId: number;
  workOrderOperationId?: number;
  rawItemId?: number; // Optional - use if category is NOT internal manufactured parts
  endgoodId?: number; // Optional - use if category IS internal manufactured parts
  rawCategoryId: number; // Required - category ID
  rawItemVariantId?: number;
}

export interface MaterialIssueUpdateRequest extends MaterialIssueCreateRequest {
  id: number;
}

export interface MaterialIssueFilters {
  status?: MaterialIssueStatus;
  issueType?: MaterialIssueType;
  branchId?: number;
  workOrderId?: number;
  workOrderOperationId?: number;
  rawItemId?: number;
  bomDetailId?: number;
  issuedBy?: string;
  receivedBy?: string;
  storeLocation?: string;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
}

export interface MaterialIssueStats {
  total: number;
  issued: number;
  returned: number;
  consumed: number;
  wasted: number;
  partiallyConsumed: number;
  cancelled: number;
  totalQuantityIssued: number;
  totalQuantityReturned: number;
  totalQuantityConsumed: number;
  totalQuantityWasted: number;
  totalCost: number;
}

class MaterialIssueApiService extends BaseApiService {

  // Get all material issues with pagination and filters
  async getMaterialIssues(
    page: number = 1, 
    pageSize: number = 10,
    filters?: MaterialIssueFilters
  ): Promise<ApiResponse<MaterialIssue[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      // Add filters to query parameters
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
      }

      const response = await this.makeRequest<ApiResponse<MaterialIssue[]>>(
        `${API_ENDPOINTS.MATERIAL_ISSUES.LIST}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      // console.error('MaterialIssueApiService: Error fetching material issues:', error);
      toast.error('Failed to fetch material issues');
      throw error;
    }
  }

  // Get material issue by ID
  async getMaterialIssueById(id: string): Promise<ApiResponse<MaterialIssue>> {
    try {
      const response = await this.makeRequest<ApiResponse<MaterialIssue>>(
        API_ENDPOINTS.MATERIAL_ISSUES.DETAIL(id)
      );
      return response;
    } catch (error) {
      toast.error('Failed to fetch material issue details');
      throw error;
    }
  }

  // Create new material issue
  async createMaterialIssue(issueData: MaterialIssueCreateRequest): Promise<ApiResponse<MaterialIssue>> {
    try {
      const response = await this.makeRequest<ApiResponse<MaterialIssue>>(
        API_ENDPOINTS.MATERIAL_ISSUES.CREATE, 
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(issueData),
        }
      );
      toast.success('Material issue created successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to create material issue');
      throw error;
    }
  }

  // Create multiple material issues in bulk
  async createBulkMaterialIssues(issuesData: MaterialIssueCreateRequest[]): Promise<ApiResponse<MaterialIssue[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<MaterialIssue[]>>(
        API_ENDPOINTS.MATERIAL_ISSUES.CREATE_BULK, 
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(issuesData),
        }
      );
      toast.success(`${issuesData.length} material issues created successfully!`);
      return response;
    } catch (error) {
      toast.error('Failed to create bulk material issues');
      throw error;
    }
  }

  // Update material issue
  async updateMaterialIssue(id: string, issueData: MaterialIssueUpdateRequest): Promise<ApiResponse<MaterialIssue>> {
    try {
      const response = await this.makeRequest<ApiResponse<MaterialIssue>>(
        API_ENDPOINTS.MATERIAL_ISSUES.UPDATE(id), 
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(issueData),
        }
      );
      toast.success('Material issue updated successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to update material issue');
      throw error;
    }
  }

  // Delete material issue
  async deleteMaterialIssue(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.MATERIAL_ISSUES.DELETE(id), 
        {
          method: HTTP_METHODS.DELETE,
        }
      );
      toast.success('Material issue deleted successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to delete material issue');
      throw error;
    }
  }

  // Search material issues with pagination
  async searchMaterialIssues(
    query: string, 
    page: number = 1, 
    pageSize: number = 10,
    filters?: MaterialIssueFilters
  ): Promise<ApiResponse<MaterialIssue[]>> {
    try {
      const params = new URLSearchParams({
        q: query,
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      // Add filters to search parameters
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
      }

      const response = await this.makeRequest<ApiResponse<MaterialIssue[]>>(
        `${API_ENDPOINTS.MATERIAL_ISSUES.SEARCH}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to search material issues');
      throw error;
    }
  }

  // Get material issues by work order
  async getMaterialIssuesByWorkOrder(
    workOrderId: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<MaterialIssue[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<MaterialIssue[]>>(
        `${API_ENDPOINTS.MATERIAL_ISSUES.BY_WORK_ORDER(workOrderId)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch work order material issues');
      throw error;
    }
  }

  // Get material issues by work order operation
  async getMaterialIssuesByWorkOrderOperation(
    workOrderOperationId: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<MaterialIssue[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<MaterialIssue[]>>(
        `${API_ENDPOINTS.MATERIAL_ISSUES.BY_WORK_ORDER_OPERATION(workOrderOperationId)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch work order operation material issues');
      throw error;
    }
  }

  // Get material issues by raw item
  async getMaterialIssuesByRawItem(
    rawItemId: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<MaterialIssue[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<MaterialIssue[]>>(
        `${API_ENDPOINTS.MATERIAL_ISSUES.BY_RAW_ITEM(rawItemId)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch raw item material issues');
      throw error;
    }
  }

  // Get material issues by BOM detail
  async getMaterialIssuesByBomDetail(
    bomDetailId: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<MaterialIssue[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<MaterialIssue[]>>(
        `${API_ENDPOINTS.MATERIAL_ISSUES.BY_BOM_DETAIL(bomDetailId)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch BOM detail material issues');
      throw error;
    }
  }

  // Get material issues by status
  async getMaterialIssuesByStatus(
    status: MaterialIssueStatus, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<MaterialIssue[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<MaterialIssue[]>>(
        `${API_ENDPOINTS.MATERIAL_ISSUES.BY_STATUS(status)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error(`Failed to fetch ${status.toLowerCase()} material issues`);
      throw error;
    }
  }

  // Get material issues by issue type
  async getMaterialIssuesByIssueType(
    issueType: MaterialIssueType, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<MaterialIssue[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<MaterialIssue[]>>(
        `${API_ENDPOINTS.MATERIAL_ISSUES.BY_ISSUE_TYPE(issueType)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error(`Failed to fetch ${issueType.toLowerCase()} material issues`);
      throw error;
    }
  }

  // Get material issues by branch
  async getMaterialIssuesByBranch(
    branchId: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<MaterialIssue[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<MaterialIssue[]>>(
        `${API_ENDPOINTS.MATERIAL_ISSUES.BY_BRANCH(branchId)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch branch material issues');
      throw error;
    }
  }

  // Get material issues by date range
  async getMaterialIssuesByDateRange(
    startDate: string,
    endDate: string,
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<MaterialIssue[]>> {
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<MaterialIssue[]>>(
        `${API_ENDPOINTS.MATERIAL_ISSUES.BY_DATE_RANGE}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch date range material issues');
      throw error;
    }
  }

  // Update material issue status
  async updateMaterialIssueStatus(id: string, status: MaterialIssueStatus): Promise<ApiResponse<MaterialIssue>> {
    try {
      const response = await this.makeRequest<ApiResponse<MaterialIssue>>(
        API_ENDPOINTS.MATERIAL_ISSUES.UPDATE_STATUS(id), 
        {
          method: HTTP_METHODS.PATCH,
          body: JSON.stringify({ status }),
        }
      );
      toast.success(`Material issue status updated to ${status.toLowerCase()}`);
      return response;
    } catch (error) {
      toast.error('Failed to update material issue status');
      throw error;
    }
  }

  // Return material
  async returnMaterial(
    id: string, 
    quantityReturned: number, 
    returnReason?: string
  ): Promise<ApiResponse<MaterialIssue>> {
    try {
      const response = await this.makeRequest<ApiResponse<MaterialIssue>>(
        API_ENDPOINTS.MATERIAL_ISSUES.RETURN_MATERIAL(id), 
        {
          method: HTTP_METHODS.PATCH,
          body: JSON.stringify({ quantityReturned, returnReason }),
        }
      );
      toast.success('Material returned successfully');
      return response;
    } catch (error) {
      toast.error('Failed to return material');
      throw error;
    }
  }

  // Consume material
  async consumeMaterial(
    id: string, 
    quantityConsumed: number, 
    consumptionReason?: string
  ): Promise<ApiResponse<MaterialIssue>> {
    try {
      const response = await this.makeRequest<ApiResponse<MaterialIssue>>(
        API_ENDPOINTS.MATERIAL_ISSUES.CONSUME_MATERIAL(id), 
        {
          method: HTTP_METHODS.PATCH,
          body: JSON.stringify({ quantityConsumed, consumptionReason }),
        }
      );
      toast.success('Material consumed successfully');
      return response;
    } catch (error) {
      toast.error('Failed to consume material');
      throw error;
    }
  }

  // Report wastage
  async reportWastage(
    id: string, 
    quantityWasted: number, 
    wastageReason: string
  ): Promise<ApiResponse<MaterialIssue>> {
    try {
      const response = await this.makeRequest<ApiResponse<MaterialIssue>>(
        API_ENDPOINTS.MATERIAL_ISSUES.REPORT_WASTAGE(id), 
        {
          method: HTTP_METHODS.PATCH,
          body: JSON.stringify({ quantityWasted, wastageReason }),
        }
      );
      toast.success('Wastage reported successfully');
      return response;
    } catch (error) {
      toast.error('Failed to report wastage');
      throw error;
    }
  }

  // Get material issue statistics
  async getMaterialIssueStats(filters?: MaterialIssueFilters): Promise<ApiResponse<MaterialIssueStats>> {
    try {
      let endpoint = API_ENDPOINTS.MATERIAL_ISSUES.GET_STATS;
      
      if (filters) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
        if (params.toString()) {
          endpoint += `?${params.toString()}`;
        }
      }

      const response = await this.makeRequest<ApiResponse<MaterialIssueStats>>(endpoint);
      return response;
    } catch (error) {
      toast.error('Failed to fetch material issue statistics');
      throw error;
    }
  }

  // Export material issues
  async exportMaterialIssues(filters?: MaterialIssueFilters, format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    try {
      let endpoint = API_ENDPOINTS.MATERIAL_ISSUES.EXPORT;
      
      if (filters) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
        params.append('format', format);
        if (params.toString()) {
          endpoint += `?${params.toString()}`;
        }
      } else {
        endpoint += `?format=${format}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081/api/v1'}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      toast.error('Failed to export material issues');
      throw error;
    }
  }

  // Helper method to extract data from API response
  extractData<T>(response: ApiResponse<T>): T {
    if (!response.success) {
      throw new Error(response.message || 'API request failed');
    }
    return response.data;
  }

  // Helper method to get material issues with extracted data
  async getMaterialIssuesData(
    page: number = 1, 
    pageSize: number = 10,
    filters?: MaterialIssueFilters
  ): Promise<{ materialIssues: MaterialIssue[], pagination: PaginationInfo }> {
    const response = await this.getMaterialIssues(page, pageSize, filters);
    const materialIssues = this.extractData(response);
    
    // Handle case where pagination is not present in response
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: materialIssues.length,
      pageSize: pageSize
    };
    
    return {
      materialIssues,
      pagination
    };
  }

  // Helper method to search material issues with extracted data
  async searchMaterialIssuesData(
    query: string, 
    page: number = 1, 
    pageSize: number = 10,
    filters?: MaterialIssueFilters
  ): Promise<{ materialIssues: MaterialIssue[], pagination: PaginationInfo }> {
    const response = await this.searchMaterialIssues(query, page, pageSize, filters);
    const materialIssues = this.extractData(response);
    
    // Handle case where pagination is not present in response
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: materialIssues.length,
      pageSize: pageSize
    };
    
    return {
      materialIssues,
      pagination
    };
  }

  // Helper method to get material issues by work order with extracted data
  async getMaterialIssuesByWorkOrderData(
    workOrderId: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<{ materialIssues: MaterialIssue[], pagination: PaginationInfo }> {
    const response = await this.getMaterialIssuesByWorkOrder(workOrderId, page, pageSize);
    const materialIssues = this.extractData(response);
    
    // Handle case where pagination is not present in response
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: materialIssues.length,
      pageSize: pageSize
    };
    
    return {
      materialIssues,
      pagination
    };
  }

  // Helper method to get material issues by status with extracted data
  async getMaterialIssuesByStatusData(
    status: MaterialIssueStatus, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<{ materialIssues: MaterialIssue[], pagination: PaginationInfo }> {
    const response = await this.getMaterialIssuesByStatus(status, page, pageSize);
    const materialIssues = this.extractData(response);
    
    // Handle case where pagination is not present in response
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: materialIssues.length,
      pageSize: pageSize
    };
    
    return {
      materialIssues,
      pagination
    };
  }
}

export const materialIssueApiService = new MaterialIssueApiService();
